import type { KeyboardEvent, ReactNode } from 'react';
import { forwardRef } from 'react';
import { TokenizedField } from '@frameio/vapor';
import { useInputTokenInteraction } from '../../contexts/InputTokenInteractionContext';
import type { InputToken } from '../../utils';
import { UserInputToken } from './UserInputToken';

type AdornmentProps =
  | {
      adornmentAfter: React.ReactNode;
      adornmentUnder?: never;
    }
  | {
      adornmentAfter?: never;
      adornmentUnder: React.ReactNode;
    };

const MAX_FIELD_HEIGHT = 126;

export type UserAccessSearchInputProps = {
  onEnterKey?: () => void;
  onInputBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onInputFocus?: () => void;
  onRemoveToken?: (id: string) => void;
  placeholder: string;
  adornmentUnder?: ReactNode;
  adornmentAfter?: ReactNode;
  tokens?: InputToken[];
  textQuery: string;
  isDisabled?: boolean;
  error?: string;
} & Pick<React.HTMLAttributes<HTMLInputElement>, 'onChange' | 'onPaste'>;

export const UserAccessSearchInput = forwardRef(
  (
    {
      onEnterKey,
      onChange,
      onPaste,
      onInputBlur,
      onInputFocus,
      onRemoveToken,
      placeholder,
      adornmentAfter,
      adornmentUnder,
      tokens,
      textQuery,
      error: errorProp,
      isDisabled = false,
    }: UserAccessSearchInputProps,
    forwardedRef
  ) => {
    const { onTokenClick } = useInputTokenInteraction();
    const adornmentProps: AdornmentProps = adornmentUnder
      ? { adornmentUnder }
      : { adornmentAfter };

    return (
      <TokenizedField
        ref={forwardedRef}
        {...adornmentProps}
        autoComplete="off"
        data-testid="project-member-invite-input"
        disabled={isDisabled}
        error={errorProp}
        maxHeight={`${MAX_FIELD_HEIGHT}px`}
        name="search"
        onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
          onInputBlur?.(event)
        }
        onChange={onChange}
        onFocus={onInputFocus}
        onKeyDown={(evt: KeyboardEvent<HTMLInputElement>) => {
          if (evt.key === 'Enter') {
            onEnterKey?.();
          }
        }}
        onPaste={onPaste}
        onTokenDelete={(index) => {
          if (tokens) {
            onRemoveToken?.(tokens[index].id);
          }
        }}
        placeholder={placeholder}
        tokens={tokens?.map((token) => {
          const onClick =
            token.isClickable && onTokenClick
              ? () => onTokenClick(token)
              : undefined;
          return (
            <UserInputToken
              key={token.id}
              {...token}
              isClickable={Boolean(onClick)}
              onClick={onClick}
            />
          );
        })}
        value={textQuery}
      />
    );
  }
);
